import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';
import { cloneInvoiceApi, fetchBusinessInvoices, putRawBusinessInvoiceApi } from '~/api';
import { useBoolean } from '~/hooks/useBoolean';
import { useUserLimits } from '~/hooks/useUserLimits';
import { useSelector } from '~/store';
import { selectBusinessUuid } from '~/store/slices/formSlice/selectors';
import { IListInvoice } from '~/types/invoice';
import { ApiResponseEnum, apiResponseHandler } from '~/utils/api';

interface InvoiceApiResponse {
  next: string | null;
  previous: string | null;
  count: number;
  total_pages: number;
  results: IListInvoice[];
}

interface State {
  listIds: string[];
  listData: Record<string, IListInvoice>;
  isLoading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  totalCount: number;
}

const initialState: State = {
  listIds: [],
  listData: {},
  isLoading: false,
  error: null,
  currentPage: 1,
  totalPages: 1,
  totalCount: 0,
};

type Action =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; payload: { invoices: InvoiceApiResponse; page: number } }
  | { type: 'FETCH_FAILURE'; error: string }
  | { type: 'RESET_LIST' }
  | { type: 'UPDATE_DATA'; payload: { invoiceUuid: string; data: Partial<IListInvoice> } };

const invoiceReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, isLoading: true, error: null };

    case 'FETCH_SUCCESS':
      const { invoices, page } = action.payload;
      const newIds = invoices.results.map(invoice => invoice.invoice_uuid);
      const newData = invoices.results.reduce<Record<string, IListInvoice>>((acc, invoice) => {
        acc[invoice.invoice_uuid] = invoice;
        return acc;
      }, {});

      return {
        ...state,
        isLoading: false,
        listIds: newIds,
        listData: newData,
        currentPage: page,
        totalPages: invoices.total_pages,
        totalCount: invoices.count,
      };

    case 'FETCH_FAILURE':
      return { ...state, isLoading: false, error: action.error };

    case 'RESET_LIST':
      return { ...initialState };

    case 'UPDATE_DATA': {
      const { invoiceUuid, data } = action.payload;
      return {
        ...state,
        listData: {
          ...state.listData,
          [invoiceUuid]: {
            ...state.listData[invoiceUuid],
            ...data,
          },
        },
      };
    }

    default:
      return state;
  }
};

export const useInvoiceList = () => {
  const [state, dispatch] = useReducer(invoiceReducer, initialState);
  const [changeToggle, changeToggleActions] = useBoolean();
  const [deleteInvoiceId, setDeleteInvoiceId] = useState<null | string>(null);
  const [viewInvoiceId, setViewInvoiceId] = useState<null | string>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const businessUuid = useSelector(selectBusinessUuid);

  // Capture viewId from URL on mount and strip it immediately
  const pendingViewId = useRef<string | null>(null);
  useEffect(() => {
    const viewId = searchParams.get('viewId');
    if (viewId) {
      pendingViewId.current = viewId;
      const cleaned = new URLSearchParams(searchParams);
      cleaned.delete('viewId');
      setSearchParams(cleaned, { replace: true });
    }
  }, []);

  // Use user limits hook
  const { canCreateInvoice, refetch: refetchUserLimits } = useUserLimits(true);

  const fetchInvoices = useCallback(
    async (page = 1, queryParams: string) => {
      dispatch({ type: 'FETCH_START' });

      const promise = fetchBusinessInvoices(businessUuid, queryParams, page);
      const response = await apiResponseHandler<InvoiceApiResponse>(promise);

      if (response.type === ApiResponseEnum.Success) {
        dispatch({ type: 'FETCH_SUCCESS', payload: { invoices: response.data, page } });
        // Open detail modal if we have a pending viewId from URL
        if (pendingViewId.current) {
          setViewInvoiceId(pendingViewId.current);
          pendingViewId.current = null;
        }
      } else {
        dispatch({ type: 'FETCH_FAILURE', error: response.error });
      }
    },
    [],
  );

  async function onEditListInvoice(invoiceUuid: string, invoiceUpdates: Partial<IListInvoice>) {
    const promise = await putRawBusinessInvoiceApi(businessUuid, invoiceUuid, invoiceUpdates);
    const response = await apiResponseHandler<IListInvoice>(promise);
    if (response.type === ApiResponseEnum.Success) {
      dispatch({ type: 'UPDATE_DATA', payload: { invoiceUuid, data: response.data } });
      toast.success('Successfully updated invoice');
      changeToggleActions.toggle();
    } else {
      toast.error('An error occurred while saving your data, please try again later');
      return;
    }
  }

  const handleStatusChange = (qsKey: string, selectedValue: string) => {
    dispatch({ type: 'RESET_LIST' });
    const newParams = new URLSearchParams(searchParams);
    if (selectedValue) {
      newParams.set(qsKey, selectedValue);
    } else {
      newParams.delete(qsKey);
    }
    setSearchParams(newParams);
  };

  const refetchList = () => {
    fetchInvoices(state.currentPage, `?${searchParams.toString()}`);
  };

  async function onCloneInvoice(invoiceUuid: string) {
    try {
      const promise = cloneInvoiceApi(businessUuid, invoiceUuid);
      const response = await apiResponseHandler(promise);

      if (response.type === ApiResponseEnum.Success) {
        toast.success('Invoice cloned successfully');
        refetchUserLimits();
        refetchList();
      } else {
        toast.error(response.error);
      }
    } catch (error) {
      toast.error('An error occurred while cloning the invoice');
    }
  }

  useEffect(() => {
    fetchInvoices(state.currentPage, `?${searchParams.toString()}`);
  }, [fetchInvoices, deleteInvoiceId, searchParams, changeToggle]);

  return {
    ...state,
    deleteInvoiceId,
    setDeleteInvoiceId,
    onEditListInvoice,
    viewInvoiceId,
    setViewInvoiceId,
    handleStatusChange,
    canCreateInvoice,
    onCloneInvoice,
    fetchNextPage: () => {
      if (state.currentPage < state.totalPages) {
        fetchInvoices(state.currentPage + 1, `?${searchParams.toString()}`);
      }
    },
    fetchSpecificPage: (page: number) => {
      if (page >= 1 && page <= state.totalPages) {
        fetchInvoices(page, `?${searchParams.toString()}`);
      }
    },
  };
};
